import React from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
} from '@mui/material';
import type { Location } from '../../types/vrp';
import { LocationRow } from './LocationRow';

interface LocationsTableProps {
  locations: Location[];
  selectedRows: number[];
  onSelectRow: (locationId: number) => void;
  onSelectAll: (checked: boolean) => void;
  onView: (location: Location) => void;
  onEdit: (location: Location) => void;
  onDelete: (location: Location) => void;
}

export const LocationsTable: React.FC<LocationsTableProps> = React.memo(({
  locations,
  selectedRows,
  onSelectRow,
  onSelectAll,
  onView,
  onEdit,
  onDelete,
}) => {
  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSelectAll(event.target.checked);
  };

  const isAllSelected = locations.length > 0 && selectedRows.length === locations.length;
  const isIndeterminate = selectedRows.length > 0 && selectedRows.length < locations.length;

  return (
    <TableContainer 
      component={Paper} 
      elevation={2}
      sx={{ 
        maxHeight: 'var(--table-container-data-management)',
        height: 'var(--table-container-data-management)'
      }}
    >
      <Table size="small" sx={{ '& .MuiTableCell-root': { py: 'var(--table-cell-padding-ultra-dense)' } }}>
        <TableHead>
          <TableRow sx={{ 
            height: 'var(--table-header-height-ultra-dense)',
            '& .MuiTableCell-root': {
              fontSize: 'var(--table-font-size-ultra-dense)',
              lineHeight: 'var(--table-line-height-ultra-dense)'
            }
          }}>
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={isIndeterminate}
                checked={isAllSelected}
                onChange={handleSelectAllChange}
                aria-label="Select all locations"
                size="small"
              />
            </TableCell>
            <TableCell sx={{ fontWeight: 'var(--table-header-font-weight)' }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 'var(--table-header-font-weight)' }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 'var(--table-header-font-weight)' }}>Address</TableCell>
            <TableCell sx={{ fontWeight: 'var(--table-header-font-weight)' }}>Coordinates</TableCell>
            <TableCell sx={{ fontWeight: 'var(--table-header-font-weight)' }}>Created</TableCell>
            <TableCell align="center" sx={{ fontWeight: 'var(--table-header-font-weight)' }}>Actions</TableCell>
          </TableRow>
        </TableHead>        <TableBody>
          {locations.map((location) => (
            <LocationRow
              key={location.id}
              location={location}
              isSelected={selectedRows.includes(location.id)}
              onSelect={onSelectRow}
              onView={onView}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
});

LocationsTable.displayName = 'LocationsTable';
